import { ensureCloudflareClearance } from "./cloudflare-clearance"

type HtmxAjax = (
  verb: string,
  path: string,
  context: { source: Element; target?: Element | null },
) => Promise<unknown>

type BeforeOnLoadDetail = {
  xhr: XMLHttpRequest
  elt: Element
  target?: Element | null
  requestConfig?: { verb: string; path: string }
}

/** One background clearance and replay per element in this interval. */
const RETRY_INTERVAL_MILLIS = 5 * 60_000

const isChallenged = (xhr: XMLHttpRequest): boolean =>
  xhr.getResponseHeader("cf-mitigated") === "challenge"

/**
 * Clear Cloudflare challenges on htmx fragment requests.
 *
 * A challenged response is cancelled before htmx handles it, so the challenge
 * page is neither swapped in nor logged as an htmx error. The request is
 * replayed from its element after background clearance. Fragments load without
 * a deliberate visitor action, so this never shows an interactive check; when
 * background clearance fails the fragment stays as it was.
 */
export const installHtmxCloudflareClearance = (
  ajax: HtmxAjax,
  root: HTMLElement = document.body,
): void => {
  const lastRetry = new WeakMap<Element, number>()
  root.addEventListener("htmx:beforeOnLoad", (event) => {
    const { xhr, elt, target, requestConfig } = (event as CustomEvent<BeforeOnLoadDetail>).detail
    if (!requestConfig || !isChallenged(xhr)) return
    event.preventDefault()
    const previous = lastRetry.get(elt)
    if (previous !== undefined && Date.now() - previous < RETRY_INTERVAL_MILLIS) return
    lastRetry.set(elt, Date.now())
    ensureCloudflareClearance({ interactive: false })
      .then(() => {
        if (elt.isConnected) {
          return ajax(requestConfig.verb, requestConfig.path, { source: elt, target })
        }
      })
      .catch(() => undefined)
  })
}
